import { useIsFeatureEnabled } from '@common/feature-flags/FeatureFlags';
import { isPerfMetricsEnabled } from '@common/metrics/journey/debugConsole';
import { ConsoleTransport, metrics, MultiTransport } from '@prophecy/metrics';
import { StableChildren } from '@prophecy/utils/react/StableChildren';
import { getBrowserSessionId, getSocketSessionId, getTabId } from '@prophecy/utils/session';
import React, { createContext, useContext, useEffect, useRef } from 'react';

import { useAppMetadata } from '../appMetadata';
import { useGlobalVariables } from '../globalConfig/securedConfigHook';
import { useUnsecuredGlobalVariables } from '../globalConfig/unsecuredConfigHook';
import { DebugEventBufferTransport } from './debugEventBuffer';
import { FetchMetricsTransport, FullStoryTransport } from './hooks';

/**
 * Thin React layer over the framework-agnostic `metrics` singleton (`@prophecy/metrics`).
 * It injects app-specific data (version, session id, feature flag, identify payload) into the
 * singleton and exposes it to React. The singleton itself is callable from non-React code too.
 *
 * NOTE: this runs alongside the existing Mixpanel tracking; neither replaces the other yet.
 *
 * Local performance-metrics debugging: `localStorage.setItem('enablePerfMetrics', '1')` (or
 * `?enablePerfMetrics=1`) adds ConsoleTransport and the debug event buffer, but only shows
 * anything when metrics are already enabled (`isUnifiedMetricsEnabled`) — it no longer forces
 * tracking on by itself. See `common/metrics/journey/debugConsole.ts`.
 */
export const MetricsContext = createContext<typeof metrics>(metrics);

export function useMetrics() {
  return useContext(MetricsContext);
}

export function MetricsProvider({ children }: { children: React.ReactNode }) {
  const appData = useAppMetadata();
  const isUnifiedMetricsEnabled =
    useGlobalVariables().isUnifiedMetricsEnabled || useUnsecuredGlobalVariables().isUnifiedMetricsEnabled;
  const isConsoleLoggingEnabled = useIsFeatureEnabled('enableMetricsConsoleLogging');
  const userId = appData?.user?.id;

  // Outlives the reconfigure effect below — isConsoleLoggingEnabled can flip live (no reload) and
  // must not wipe the perf-debug event buffer a developer is relying on mid-session.
  const debugEventBufferRef = useRef<DebugEventBufferTransport>();
  useEffect(() => () => debugEventBufferRef.current?.destroy(), []);

  // Configure framework-level fields once the flag is known.
  useEffect(() => {
    const perfMetricsEnabled = isPerfMetricsEnabled();
    // Fans each event out to the backend and to FullStory, plus the console when the
    // "Allow Metrics Logging to Console" cmd-k flag or the perf-metrics debug flag is on.
    const transports = [new FetchMetricsTransport(), new FullStoryTransport()];
    if (isConsoleLoggingEnabled || perfMetricsEnabled) {
      transports.unshift(new ConsoleTransport());
    }
    // window._perfMetrics() — every tracked event this session, not just one journey's deltas.
    if (perfMetricsEnabled) {
      debugEventBufferRef.current ??= new DebugEventBufferTransport();
      const debugEventBuffer = debugEventBufferRef.current;
      // A plain wrapper, not the instance itself — MultiTransport.destroy() runs on every
      // reconfigure below, and must not tear down the long-lived buffer (the ref's unmount
      // cleanup above owns that).
      transports.unshift({ send: (event) => debugEventBuffer.send(event) });
    }
    const transport = new MultiTransport(transports);
    metrics.configure({
      service: 'ui',
      version: process.env.RELEASE_VERSION ?? 'unknown',
      environment: window.location.hostname,
      enabled: Boolean(isUnifiedMetricsEnabled),
      transport,
      getIds: () => ({
        sessionId: getBrowserSessionId(),
        socketId: getSocketSessionId(),
        tabId: getTabId()
      })
    });
    return () => transport.destroy();
  }, [isUnifiedMetricsEnabled, isConsoleLoggingEnabled]);

  useEffect(() => {
    if (!userId) return;
    metrics.identify({ userId });
  }, [userId]);

  return (
    <MetricsContext.Provider value={metrics}>
      <StableChildren>{children}</StableChildren>
    </MetricsContext.Provider>
  );
}
